import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Spinner, Alert, Card, Button, Row, Col, Form, Table, InputGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch,
  faFilter,
  faTimes,
  faEye,
  faCog,
  faCalendarAlt,
  faList,
  faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Event, EventColor } from '../interfaces';
import moment from 'moment';
import { API_BASE_URL, buildApiUrl } from '../config/api';

const STATUS_LABELS: { [key: string]: string } = {
    'planned': 'מתוכנן',
    'preparation': 'בהכנה',
    'active': 'פעיל/בחוץ',
    'completed': 'הסתיים',
    'cancelled': 'בוטל',
    'no_equipment_list': 'ללא רשימת ציוד',
};

const STATUS_COLORS: { [key: string]: string } = {
    'planned': 'primary',
    'preparation': 'warning',
    'active': 'success',
    'completed': 'secondary',
    'cancelled': 'danger',
    'no_equipment_list': 'light',
};

interface EventsSearchPageProps {
    onBack: () => void;
    onEventDetails: (eventId: number) => void;
    onManageEquipment: (eventId: number) => void;
    onEventPreparation: (eventId: number) => void;
}

const EventsSearchPage: React.FC<EventsSearchPageProps> = ({ 
    onBack, 
    onEventDetails, 
    onManageEquipment, 
    onEventPreparation 
}) => {
    const { user } = useAuth();
    // State for events data
    const [events, setEvents] = useState<Event[]>([]);
    const [eventColors, setEventColors] = useState<EventColor[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    // Search and filter state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>(['planned', 'preparation', 'active', 'no_equipment_list']);
    const [startDateFilter, setStartDateFilter] = useState<string>('');
    const [endDateFilter, setEndDateFilter] = useState<string>('');
    const [showFilters, setShowFilters] = useState<boolean>(false);

    // View settings
    const [viewMode, setViewMode] = useState<'list' | 'table'>('table');
    const [sortBy, setSortBy] = useState<'date' | 'name' | 'status'>('date');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

    // Fetch events data
    const fetchEvents = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [eventsRes, colorsRes] = await Promise.all([
                axios.get(buildApiUrl('events/events/')),
                axios.get(buildApiUrl('settings/event-colors/'))
            ]);
            
            setEvents(eventsRes.data);
            setEventColors(colorsRes.data);
        } catch (err) {
            setError("לא ניתן לטעון את רשימת האירועים.");
            console.error("Error fetching events:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    // Filter and sort events
    const filteredAndSortedEvents = useMemo(() => {
        let filtered = [...events];

        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            filtered = filtered.filter(event => 
                event.event_name.toLowerCase().includes(searchLower) ||
                (event.client_name && event.client_name.toLowerCase().includes(searchLower)) ||
                (event.free_text_client && event.free_text_client.toLowerCase().includes(searchLower)) ||
                (event.location_name && event.location_name.toLowerCase().includes(searchLower)) ||
                (event.free_text_location && event.free_text_location.toLowerCase().includes(searchLower)) ||
                (event.description && event.description.toLowerCase().includes(searchLower))
            );
        }

        // Status filter
        if (selectedStatuses.length > 0) {
            filtered = filtered.filter(event => selectedStatuses.includes(event.status));
        }

        // Date range filter
        if (startDateFilter) {
            filtered = filtered.filter(event => {
                const eventStartDate = new Date(event.start_date);
                const filterStartDate = new Date(startDateFilter);
                return eventStartDate >= filterStartDate;
            });
        }

        if (endDateFilter) {
            filtered = filtered.filter(event => {
                const eventStartDate = new Date(event.start_date);
                const filterEndDate = new Date(endDateFilter);
                return eventStartDate <= filterEndDate;
            });
        }

        // Sort events
        filtered.sort((a, b) => {
            let comparison = 0;
            
            switch (sortBy) {
                case 'date':
                    comparison = new Date(a.start_date).getTime() - new Date(b.start_date).getTime();
                    break;
                case 'name':
                    comparison = a.event_name.localeCompare(b.event_name);
                    break;
                case 'status':
                    comparison = a.status.localeCompare(b.status);
                    break;
            }
            
            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return filtered;
    }, [events, searchTerm, selectedStatuses, startDateFilter, endDateFilter, sortBy, sortOrder]);

    // Handle status toggle
    const handleStatusToggle = (status: string) => {
        setSelectedStatuses(prev => {
            if (prev.includes(status)) {
                const newStatuses = prev.filter(s => s !== status);
                // Ensure at least one status is selected
                return newStatuses.length > 0 ? newStatuses : prev;
            } else {
                return [...prev, status];
            }
        });
    };

    // Clear all filters
    const clearFilters = () => {
        setSearchTerm('');
        setSelectedStatuses(['planned', 'preparation', 'active', 'no_equipment_list']);
        setStartDateFilter('');
        setEndDateFilter('');
    };

    // Format date for display
    const formatDate = (dateString: string) => {
        return moment(dateString).format('DD/MM/YYYY');
    };

    // Get event color
    const getEventColor = (event: Event) => {
        if (event.color_hex) {
            return event.color_hex;
        }
        return undefined;
    };

    if (loading) {
        return (
            <Container fluid className="mt-4">
                <div className="text-center">
                    <Spinner animation="border" />
                    <p className="mt-3">טוען אירועים...</p>
                </div>
            </Container>
        );
    }

    if (error) {
        return (
            <Container fluid className="mt-4">
                <Alert variant="danger">
                    <Alert.Heading>שגיאה בטעינת האירועים</Alert.Heading>
                    <p>{error}</p>
                    <Button variant="outline-danger" onClick={fetchEvents}>
                        <FontAwesomeIcon icon={faTimes} className="me-1" />
                        נסה שוב
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid>
            <div className="d-flex justify-content-between align-items-center mb-4 app-sticky-page-toolbar">
                <h2>חיפוש וסינון אירועים</h2>
                <Button variant="secondary" onClick={onBack}>
                    <FontAwesomeIcon icon={faArrowRight} className="me-1" />
                    חזור
                </Button>
            </div>

            {/* Search and Filter Controls */}
            <Card className="mb-4">
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">חיפוש וסינון</h5>
                        <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FontAwesomeIcon icon={faFilter} className="me-1" />
                            {showFilters ? 'הסתר סינונים' : 'הצג סינונים'}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {/* Basic Search */}
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Group>
                                <Form.Label>חיפוש</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text><FontAwesomeIcon icon={faSearch} /></InputGroup.Text>
                                    <Form.Control
                                        placeholder="חפש לפי שם אירוע, לקוח, מיקום או תיאור..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>מיון</Form.Label>
                                <div className="d-flex gap-2">
                                    <Form.Select 
                                        size="sm" 
                                        value={sortBy} 
                                        onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'status')}
                                    >
                                        <option value="date">תאריך</option>
                                        <option value="name">שם אירוע</option>
                                        <option value="status">סטטוס</option>
                                    </Form.Select>
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                                        title={sortOrder === 'asc' ? 'מיון עולה' : 'מיון יורד'}
                                        style={{ fontSize: '1.5rem', lineHeight: 1.1 }}
                                    >
                                        {sortOrder === 'asc' ? '↑' : '↓'}
                                    </Button>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <div className="border-top pt-3">
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>סטטוס</Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {Object.entries(STATUS_LABELS).map(([status, label]) => (
                                                <Form.Check
                                                    key={status}
                                                    type="checkbox"
                                                    id={`status-${status}`}
                                                    label={label}
                                                    checked={selectedStatuses.includes(status)}
                                                    onChange={() => handleStatusToggle(status)}
                                                    className="mb-1"
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>תאריך התחלה</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={startDateFilter}
                                            onChange={(e) => setStartDateFilter(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>תאריך סיום</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={endDateFilter}
                                            onChange={(e) => setEndDateFilter(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <div className="d-flex justify-content-between">
                                <Button 
                                    variant="outline-secondary" 
                                    size="sm" 
                                    onClick={clearFilters}
                                >
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    נקה כל הסינונים
                                </Button>
                                <div className="text-muted small">
                                    נמצאו {filteredAndSortedEvents.length} אירועים מתוך {events.length}
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Results */}
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">תוצאות ({filteredAndSortedEvents.length})</h5>
                        <div className="btn-group" role="group">
                            <Button
                                size="sm"
                                variant={viewMode === 'table' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('table')}
                            >
                                <FontAwesomeIcon icon={faList} className="me-1" />
                                טבלה
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                                onClick={() => setViewMode('list')}
                            >
                                <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                כרטיסים
                            </Button>
                        </div>
                    </div>
                </Card.Header>
                <Card.Body>
                    {filteredAndSortedEvents.length === 0 ? (
                        <div className="text-center py-5">
                            <FontAwesomeIcon icon={faSearch} size="3x" className="text-muted mb-3" />
                            <h5>לא נמצאו אירועים</h5>
                            <p className="text-muted">נסה לשנות את תנאי החיפוש או הסינון</p>
                        </div>
                    ) : viewMode === 'table' ? (
                        <Table striped hover responsive>
                            <thead>
                                <tr>
                                    <th>אירוע</th>
                                    <th>לקוח</th>
                                    <th>מיקום</th>
                                    <th>תאריכים</th>
                                    <th>סטטוס</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAndSortedEvents.map(event => (
                                    <tr key={event.id} style={{ cursor: 'pointer' }} onClick={() => onEventDetails(event.id)}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {getEventColor(event) && (
                                                    <div
                                                        className="me-2"
                                                        style={{
                                                            width: '12px',
                                                            height: '12px',
                                                            backgroundColor: getEventColor(event),
                                                            borderRadius: '50%',
                                                            border: '1px solid #ccc'
                                                        }}
                                                        title={event.color_name}
                                                    />
                                                )}
                                                <strong style={{ cursor: 'pointer' }} onClick={() => onEventDetails(event.id)} title="פרטי אירוע">{event.event_name}</strong>
                                            </div>
                                        </td>
                                        <td>{event.client_name || event.free_text_client || '-'}</td>
                                        <td>{event.location_name || event.free_text_location || '-'}</td>
                                        <td>
                                            {formatDate(event.start_date)}
                                            {event.start_date !== event.end_date && (
                                                <span className="text-muted"> - {formatDate(event.end_date)}</span>
                                            )}
                                        </td>
                                        <td>
                                            {event.status === 'no_equipment_list' ? (
                                                <span className="badge" style={{ backgroundColor: '#f8bbd0', color: '#000' }}>
                                                    {STATUS_LABELS[event.status]}
                                                </span>
                                            ) : (
                                                <Badge bg={STATUS_COLORS[event.status]}>
                                                    {STATUS_LABELS[event.status]}
                                                </Badge>
                                            )}
                                        </td>
                                        <td>
                                            <div className="d-flex gap-1">
                                                <Button
                                                    size="sm"
                                                    variant="info"
                                                    onClick={() => onEventDetails(event.id)}
                                                    title="פרטי אירוע"
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    פרטים
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => onManageEquipment(event.id)}
                                                    disabled={user?.role?.name === 'TECHNICIAN'}
                                                    title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לניהול ציוד' : 'ניהול ציוד'}
                                                >
                                                    <FontAwesomeIcon icon={faCog} className="me-1" />
                                                    ציוד
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => onEventPreparation(event.id)}
                                                    disabled={user?.role?.name === 'TECHNICIAN'}
                                                    title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה להכנת/העמסת אירועים' : 'הכנה/העמסה'}
                                                    style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                                    הכנה
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    ) : (
                        <div className="row">
                            {filteredAndSortedEvents.map(event => (
                                <div key={event.id} className="col-md-6 col-lg-4 mb-3">
                                    <Card className="h-100" onClick={() => onEventDetails(event.id)} style={{ cursor: 'pointer' }}>
                                        <Card.Body>
                                            <div className="d-flex justify-content-between align-items-start mb-2">
                                                <h6 className="card-title mb-0">
                                                    <div className="d-flex align-items-center">
                                                        {getEventColor(event) && (
                                                            <div
                                                                className="me-2"
                                                                style={{
                                                                    width: '12px',
                                                                    height: '12px',
                                                                    backgroundColor: getEventColor(event),
                                                                    borderRadius: '50%',
                                                                    border: '1px solid #ccc'
                                                                }}
                                                                title={event.color_name}
                                                            />
                                                        )}
                                                        <span style={{ cursor: 'pointer' }} onClick={() => onEventDetails(event.id)} title="פרטי אירוע">{event.event_name}</span>
                                                    </div>
                                                </h6>
                                                {event.status === 'no_equipment_list' ? (
                                                    <span className="badge" style={{ backgroundColor: '#f8bbd0', color: '#000' }}>
                                                        {STATUS_LABELS[event.status]}
                                                    </span>
                                                ) : (
                                                    <Badge bg={STATUS_COLORS[event.status]}>
                                                        {STATUS_LABELS[event.status]}
                                                    </Badge>
                                                )}
                                            </div>
                                            
                                            <div className="small text-muted mb-2">
                                                <div><strong>לקוח:</strong> {event.client_name || event.free_text_client || '-'}</div>
                                                <div><strong>מיקום:</strong> {event.location_name || event.free_text_location || '-'}</div>
                                                <div><strong>תאריכים:</strong> {formatDate(event.start_date)}
                                                    {event.start_date !== event.end_date && (
                                                        <span> - {formatDate(event.end_date)}</span>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="d-flex gap-1 mt-3">
                                                <Button
                                                    size="sm"
                                                    variant="info"
                                                    onClick={(e) => { e.stopPropagation(); onEventDetails(event.id); }}
                                                    className="flex-fill"
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                    פרטים
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="primary"
                                                    onClick={() => onManageEquipment(event.id)}
                                                    disabled={user?.role?.name === 'TECHNICIAN'}
                                                    title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה לניהול ציוד' : 'ניהול ציוד'}
                                                    className="flex-fill"
                                                >
                                                    <FontAwesomeIcon icon={faCog} className="me-1" />
                                                    ציוד
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="secondary"
                                                    onClick={() => onEventPreparation(event.id)}
                                                    disabled={user?.role?.name === 'TECHNICIAN'}
                                                    title={user?.role?.name === 'TECHNICIAN' ? 'אין לך הרשאה להכנת/העמסת אירועים' : 'הכנה/העמסה'}
                                                    className="flex-fill"
                                                    style={{ backgroundColor: '#8A2BE2', borderColor: '#8A2BE2' }}
                                                >
                                                    <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                                                    הכנה
                                                </Button>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </div>
                            ))}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default EventsSearchPage;
